import { motion } from 'framer-motion';
import { useContent } from '@/context/ContentContext';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import HeroCanvas from './HeroCanvas';

// Masked line reveal (each line wrapped in overflow-hidden, span animates from y:100%)
const lineReveal = {
  hidden: { y: '110%' },
  show: (i) => ({
    y: '0%',
    transition: {
      duration: 1.05,
      ease: [0.76, 0, 0.24, 1],
      delay: 0.35 + i * 0.12,
    },
  }),
};

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay },
  },
});

export default function Hero() {
  const { t } = useContent();
  if (!t) return null;
  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] w-full overflow-hidden pt-20 sm:pt-24 md:pt-28"
    >
      <HeroCanvas />

      {/* Top meta row */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp(0.1)}
          className="flex items-center justify-between font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.28em] text-ink-dim gap-4"
        >
          <span className="truncate">{t.hero.eyebrow}</span>
          <span className="hidden md:inline flex-shrink-0">Est. 2022 · Cyprus ⇢ Everywhere</span>
        </motion.div>

        <div className="hairline mt-4" />
      </div>

      {/* Kinetic headline */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 pt-10 sm:pt-14 md:pt-24">
        <h1
          data-testid="hero-headline"
          className="font-serif text-[11.5vw] sm:text-[10.5vw] md:text-[9.5vw] leading-[0.94] md:leading-[0.92] tracking-[-0.03em] md:tracking-[-0.035em]"
        >
          {lines.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                animate="show"
                custom={i}
                className={i === 2 ? 'italic text-ember' : ''}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 sm:mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end">
          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp(1.3)}
            className="md:col-span-6 md:col-start-6 text-ink text-sm sm:text-base md:text-lg leading-relaxed max-w-xl"
          >
            {t.hero.sub}
          </motion.p>
        </div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp(1.55)}
          className="mt-10 sm:mt-12 md:mt-16 flex flex-wrap items-center gap-3 sm:gap-4"
        >
          <a href="#contact" data-testid="hero-cta-primary" className="btn-ember">
            {t.hero.cta_primary}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <a href="#process" data-testid="hero-cta-secondary" className="btn-ghost">
            {t.hero.cta_secondary}
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </a>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1.2 }}
        className="absolute bottom-4 md:bottom-10 left-4 sm:left-6 md:left-10 z-10 flex items-center gap-3 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.28em] sm:tracking-[0.3em] text-ink-dim"
      >
        <span className="inline-block w-6 sm:w-8 h-px bg-ember" />
        Scroll
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1.2 }}
        className="absolute bottom-4 md:bottom-10 right-4 sm:right-6 md:right-10 z-10 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.28em] sm:tracking-[0.3em] text-ink-dim text-right"
      >
        Rotama Co · Ltd
        <br />
        <span className="text-ember">Vol. 01</span>
      </motion.div>
    </section>
  );
}
